import { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext({ theme: "light", toggleTheme: () => {}, setTheme: () => {} });

// Routes where dark mode is allowed. Anything else (landing, registration,
// login, patient dashboard, sitegate, refer-a-friend page, etc.) is FORCED
// to light mode regardless of the user's saved preference. This keeps the
// public marketing surface consistent with the brand and prevents a stray
// staff preference from spilling onto a patient's first impression.
const STAFF_ROUTE_PREFIXES = ["/admin", "/prescriber", "/cliniclogin", "/doctordoctor", "/superadmin", "/it", "/staff-chat"];

const isStaffRoute = () => {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname || "";
  return STAFF_ROUTE_PREFIXES.some((p) => path === p || path.startsWith(p + "/") || path.startsWith(p));
};

// Dark mode is a staff-dashboard feature, not a brand aesthetic for the public
// marketing/patient-facing site. We default everyone to LIGHT and only honor
// dark mode when the user explicitly clicks the ThemeToggle button (which sets
// `glpflo-theme-manual`) AND the user is currently on a staff route.
export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return "light";
    if (localStorage.getItem("glpflo-theme-manual")) {
      const saved = localStorage.getItem("glpflo-theme");
      if (saved === "dark" || saved === "light") return saved;
    }
    return "light";
  });

  useEffect(() => {
    const apply = () => {
      const root = document.documentElement;
      const allow_dark = isStaffRoute();
      const effective = allow_dark && theme === "dark" ? "dark" : "light";
      // GUARD: only mutate the DOM when the effective theme has actually
      // changed. Without this, the 600 ms re-apply tick below was firing
      // `setAttribute("data-theme", ...)` on every interval which triggered
      // a style recalculation on <html>. That recalc propagated to any
      // ResizeObserver attached lower in the tree (e.g. CookieBanner's
      // measure observer), kicking off an infinite render loop visible as
      // hundreds of "Maximum update depth exceeded" warnings — and locking
      // out every left-click on the page while React was busy reconciling.
      const currentTheme = root.getAttribute("data-theme");
      if (currentTheme === effective) return;
      if (effective === "dark") {
        root.classList.add("dark");
        root.setAttribute("data-theme", "dark");
      } else {
        root.classList.remove("dark");
        root.setAttribute("data-theme", "light");
      }
      try { localStorage.setItem("glpflo-theme", theme); } catch { /* ignore */ }
    };
    apply();
    // Re-apply on SPA navigation. React Router uses pushState, so listen for
    // both popstate (back/forward) and a small interval to catch route changes
    // that fire from useNavigate.
    window.addEventListener("popstate", apply);
    const id = setInterval(apply, 600);
    return () => {
      window.removeEventListener("popstate", apply);
      clearInterval(id);
    };
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (next !== "dark" && next !== "light") return;
    try { localStorage.setItem("glpflo-theme-manual", "1"); } catch { /* ignore */ }
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((t) => {
      const next = t === "dark" ? "light" : "dark";
      try { localStorage.setItem("glpflo-theme-manual", "1"); } catch { /* ignore */ }
      return next;
    });
  }, []);

  return <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
